import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Chip,
  Link,
  Stack,
  Typography
} from '@mui/material';
import { LAB_LANE_LIST } from './mroLabLanes';

function MroLabShell({
  title,
  subtitle,
  accent = '#e65100',
  counts = {},
  actions = null,
  children
}) {
  const location = useLocation();

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: 'linear-gradient(180deg, #f7f8fa 0%, #ffffff 42%)',
        pb: 4
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          px: { xs: 2, md: 3 },
          pt: 2.5,
          pb: 0
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Breadcrumbs sx={{ mb: 0.75 }}>
              <Link
                component={RouterLink}
                to="/mro/lab"
                underline="hover"
                color="text.secondary"
                variant="body2"
              >
                MRO Workbench Lab
              </Link>
              <Typography variant="body2" color="text.primary">
                {title}
              </Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: accent,
                  flexShrink: 0
                }}
              />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                {title}
              </Typography>
              <Chip
                size="small"
                label="Spectator preview · read-only"
                sx={{
                  height: 24,
                  bgcolor: 'rgba(0,0,0,0.04)',
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              />
            </Stack>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {actions}
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            overflowX: 'auto',
            pb: 0,
            mb: '-1px'
          }}
        >
          <Chip
            component={RouterLink}
            to="/mro/lab"
            clickable
            label="Overview"
            variant={location.pathname === '/mro/lab' ? 'filled' : 'outlined'}
            color={location.pathname === '/mro/lab' ? 'default' : 'default'}
            sx={{
              borderRadius: '10px 10px 0 0',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderBottom: location.pathname === '/mro/lab' ? '2px solid' : '1px solid',
              borderBottomColor: location.pathname === '/mro/lab' ? 'text.primary' : 'divider',
              bgcolor: location.pathname === '/mro/lab' ? '#fff' : 'transparent',
              fontWeight: location.pathname === '/mro/lab' ? 600 : 500,
              '&:hover': { bgcolor: location.pathname === '/mro/lab' ? '#fff' : 'action.hover' }
            }}
          />
          {LAB_LANE_LIST.map((lane) => {
            const active = location.pathname === lane.path;
            const count = counts[lane.key];
            return (
              <Chip
                key={lane.key}
                component={RouterLink}
                to={lane.path}
                clickable
                label={typeof count === 'number' ? `${lane.title} (${count})` : lane.title}
                sx={{
                  borderRadius: '10px 10px 0 0',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  borderBottom: active ? '2px solid' : '1px solid',
                  borderBottomColor: active ? lane.accent : 'divider',
                  bgcolor: active ? '#fff' : 'transparent',
                  color: active ? lane.accent : 'text.secondary',
                  fontWeight: active ? 700 : 500,
                  '&:hover': { bgcolor: active ? '#fff' : lane.accentSoft }
                }}
              />
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 3, maxWidth: 1280, mx: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
}

export default MroLabShell;
